// src/components/research/callout.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* A labelled block, set aside from the running text.
   ------------------------------------------------------------------------
   One of three ways this page marks something out, and the three are a set.
   Anything added later should be one of these rather than a fourth shape:

     Quote    the argument itself. An accent rule and larger type, no label,
              because a claim is not a kind of thing, it is the point.
     Callout  evidence or an idea set beside the argument, long enough to be
              read on its own. Boxed, with a tab naming what kind it is.
     Note     one sentence attached to the line above it. A hairline and small
              type, small enough to sit inside a list item.

   The label is a required decision, not decoration. An observation, an idea
   and something the reader can check on themselves are different kinds of
   support, and a reader is entitled to know which one they have been handed.
   Callout does not default it for that reason.

   The tab is in normal flow rather than translated out of it. Lifting it with
   a transform meant the space it occupies had to be reserved by hand, and any
   change to its padding silently broke that reservation. In flow it takes its
   own room. Same translucent surface as the box, so the two are one colour. */
const StyledCallout = styled.aside`
  max-width: var(--measure);
  margin: var(--space-6) 0 var(--space-6);

  /* Direct child only. As a descendant selector this also caught the label
     inside a figure caption sitting in the box, and dressed it up as a second
     tab. */

  & > .label {
    display: inline-block;
    position: relative;
    z-index: 1;
    margin-bottom: -1px;
    padding: 12px 12px 7px;
    border: 1px solid var(--border);
    border-bottom: 0;
    border-left: 2px solid var(--accent);
    border-radius: var(--border-radius) var(--border-radius) 0 0;
    background-color: var(--surface-transparent);
    ${({ theme }) => theme.mixins.glass};
    color: var(--accent);
    font-family: var(--font-sans);
    font-size: var(--fz-xxs);
    font-weight: 600;
    letter-spacing: 0.12em;
    line-height: var(--lh-control);
    text-transform: uppercase;
    white-space: nowrap;
  }

  .box {
    padding: var(--space-5);
    border: 1px solid var(--border);
    border-left: 2px solid var(--accent);
    /* Square at the top left so the tab continues the corner. */
    border-radius: 0 var(--border-radius) var(--border-radius) var(--border-radius);
    background-color: var(--surface-transparent);
    ${({ theme }) => theme.mixins.glassPanel};
  }

  p {
    margin: 0 0 var(--space-3);
    color: var(--text-secondary);
    font-size: var(--fz-lg);
    line-height: var(--lh-body);

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: var(--text-emphasis);
      font-weight: 500;
    }
  }
`;

const Callout = ({ label, children }) => (
  <StyledCallout>
    <span className="label">{label}</span>
    <div className="box">{children}</div>
  </StyledCallout>
);

Callout.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default Callout;
